import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ComponentCanDeactivate } from '@app/shared/guards/unsaved-changes.guard';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { TimePickerComponent } from '@app/partials/form/time-picker/time-picker.component';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { EventTemplateService } from '../shared/event-template.service';
import { EventTemplateModel, EventTemplateModelUtils } from '../shared/event-template.model';
import { EventTemplateItemModel, EventTemplateItemModelUtils } from '../shared/event-template-item.model';
import { NotificationService } from '@app/shared/notification/notification.service';
import { takeUntil } from 'rxjs';
import { LucideAngularModule, Plus, Trash2, X, CheckCircle } from 'lucide-angular';
import { VenueService } from '@app/venues/shared/venue.service';
import { VenueModel } from '@app/venues/shared/venue.model';
import { RouteParams } from '@app/shared/abstract/route-params';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';

@Component({
  selector: 'app-event-template-edit',
  templateUrl: './event-template-edit.component.html',
  imports: [
    ButtonComponent,
    SelectComponent,
    TextInputComponent,
    TextareaComponent,
    TimePickerComponent,
    ReactiveFormsModule,
    FormsModule,
    LucideAngularModule,
  ],
  standalone: true
})
export class EventTemplateEditComponent extends Destroyer implements OnInit, ComponentCanDeactivate {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventTemplateService: EventTemplateService,
    private notificationService: NotificationService,
    private venueService: VenueService,
    private confirmationModal: ConfirmationModalService,
  ) {
    super();
  }

  protected readonly PlusIcon = Plus;
  protected readonly Trash2Icon = Trash2;
  protected readonly XIcon = X;
  protected readonly CheckCircleIcon = CheckCircle;

  template: EventTemplateModel | null = null;
  saving = false;
  isNew = false;
  venues: VenueModel[] = [];

  templateForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    event_type: new FormControl('Wedding', [Validators.required]),
    venue_id: new FormControl<number | null>(null),
    description: new FormControl(''),
    items: new FormArray([])
  });

  eventTypes = EventTemplateModelUtils.getEventTypes().map(type => ({
    value: type,
    label: type
  }));

  get venueChoices() {
    return [
      { value: null, label: 'All Venues' },
      ...this.venues.map(venue => ({ value: venue.id, label: venue.name }))
    ];
  }

  get itemsFormArray(): FormArray {
    return this.templateForm.get('items') as FormArray;
  }

  get nameControl(): FormControl {
    return this.templateForm.get('name') as FormControl;
  }

  get eventTypeControl(): FormControl {
    return this.templateForm.get('event_type') as FormControl;
  }

  get descriptionControl(): FormControl {
    return this.templateForm.get('description') as FormControl;
  }

  get venueIdControl(): FormControl {
    return this.templateForm.get('venue_id') as FormControl;
  }

  getItemControl(index: number, controlName: string): FormControl {
    return this.itemsFormArray.at(index).get(controlName) as FormControl;
  }

  ngOnInit(): void {
    // Load venues
    const venueParams = new RouteParams({ pageSize: 100 });
    this.venueService.getPage(venueParams).pipe(takeUntil(this.destroy$)).subscribe(response => {
      this.venues = response.data;
    });

    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data['template']?.data) {
        this.template = data['template'].data;
        this.isNew = !this.template?.id;
        this.populateForm();
      }
    });
  }

  private populateForm(): void {
    if (!this.template) return;

    this.templateForm.patchValue({
      name: this.template.name,
      event_type: this.template.event_type,
      venue_id: this.template.venue_id,
      description: this.template.description
    });

    // Clear existing items
    while (this.itemsFormArray.length > 0) {
      this.itemsFormArray.removeAt(0);
    }

    // Add template items
    if (this.template.items) {
      this.template.items
        .sort((a, b) => {
          // First sort by days_offset, then by time
          if (a.days_offset !== b.days_offset) {
            return a.days_offset - b.days_offset;
          }
          return a.time_of_day.localeCompare(b.time_of_day);
        })
        .forEach(item => this.addItemToForm(item));
    }
  }

  private createItemFormGroup(item?: EventTemplateItemModel): FormGroup {
    // For new items, use sensible defaults
    const offsetAmount = item?.offset_amount !== undefined ? item.offset_amount : 1;
    const offsetUnit = item?.offset_unit || 'days';
    const offsetDirection = item?.offset_direction || 'before';
    
    // Calculate days_offset from the offset fields
    const daysOffset = item?.days_offset !== undefined ? item.days_offset : EventTemplateItemModelUtils.calculateDaysOffset(offsetAmount, offsetUnit, offsetDirection);
    
    const formGroup = new FormGroup({
      id: new FormControl(item?.id || null),
      name: new FormControl(item?.name || '', [Validators.required]),
      description: new FormControl(item?.description || ''),
      days_offset: new FormControl(daysOffset, [Validators.required]),
      time_of_day: new FormControl(item?.time_of_day || '09:00:00', [Validators.required]),
      offset_amount: new FormControl(offsetAmount, [Validators.required, Validators.min(0)]),
      offset_unit: new FormControl(offsetUnit, [Validators.required]),
      offset_direction: new FormControl(offsetDirection, [Validators.required])
    });

    return formGroup;
  }

  addItem(): void {
    this.addItemToForm();
  }

  private addItemToForm(item?: EventTemplateItemModel): void {
    const itemGroup = this.createItemFormGroup(item);

    // Listen for changes to offset fields to recalculate days_offset
    const index = this.itemsFormArray.length; // Get the index before pushing
    itemGroup.get('offset_amount')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onOffsetChange(index);
    });
    itemGroup.get('offset_unit')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onOffsetChange(index);
    });
    itemGroup.get('offset_direction')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onOffsetChange(index);
    });

    this.itemsFormArray.push(itemGroup);
  }

  removeItem(index: number): void {
    this.itemsFormArray.removeAt(index);
  }

  // Items are now sorted automatically by days_offset and time_of_day

  formatDaysOffset(offset: number): string {
    return EventTemplateItemModelUtils.formatDaysOffset(offset);
  }

  formatOffsetDisplay(itemControl: any): string {
    const amount = itemControl.get('offset_amount')?.value || 0;
    const unit = itemControl.get('offset_unit')?.value || 'days';
    const direction = itemControl.get('offset_direction')?.value || 'before';
    return EventTemplateItemModelUtils.formatOffsetDisplay(amount, unit, direction);
  }

  onOffsetChange(index: number): void {
    const itemControl = this.itemsFormArray.at(index);
    const amount = itemControl.get('offset_amount')?.value || 0;
    const unit = itemControl.get('offset_unit')?.value || 'days';
    const direction = itemControl.get('offset_direction')?.value || 'before';

    const daysOffset = EventTemplateItemModelUtils.calculateDaysOffset(amount, unit, direction);
    itemControl.get('days_offset')?.setValue(daysOffset, { emitEvent: false });
  }


  save(): void {
    this.performSave(false);
  }

  saveAndContinue(): void {
    this.performSave(true);
  }

  onSubmit(): void {
    this.performSave(false);
  }

  private performSave(continueEditing: boolean): void {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    const formValue = this.templateForm.value;

    const templateData: EventTemplateModel = {
      id: this.template?.id as number || null as unknown as number,
      name: formValue.name!,
      event_type: formValue.event_type as EventTemplateModel['event_type'],
      venue_id: formValue.venue_id || null,
      description: formValue.description || '',
      created_by: this.template?.created_by || 0,
      updated_by: this.template?.updated_by || 0,
      created_at: this.template?.created_at || null,
      updated_at: this.template?.updated_at || null,
      items: formValue.items?.map((item: any) => ({
        id: item.id,
        template_id: this.template?.id || 0,
        name: item.name,
        description: item.description,
        days_offset: Number(item.days_offset),
        time_of_day: item.time_of_day,
        offset_amount: Number(item.offset_amount),
        offset_unit: item.offset_unit,
        offset_direction: item.offset_direction,
        created_by: 0,
        updated_by: 0,
        created_at: null,
        updated_at: null
      })) || []
    };

    this.eventTemplateService.saveWithItems(templateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.saving = false;
          this.templateForm.markAsPristine();
          this.notificationService.success(
            this.isNew
              ? 'Template created successfully'
              : 'Template updated successfully'
          );

          if (continueEditing) {
            // For saveAndContinue, stay on edit page but navigate to edit mode if it was a new template
            if (this.isNew && response.data) {
              this.router.navigate(['/event-templates', response.data.id, 'edit']);
            }
            // If editing existing template, just stay on current page (no navigation)
          } else {
            // For regular save, go to view page
            this.router.navigate(['/event-templates', response.data.id]);
          }
        },
        error: (error) => {
          this.saving = false;
          if (error.status === 422 && error.error?.errors) {
            AbstractEntityEditComponent.bindErrorsToForm(error.error, this.templateForm);
            this.notificationService.error('Please fix the validation errors below');
          } else {
            this.notificationService.error('Failed to save template');
          }
          console.error('Error saving template:', error);
        }
      });
  }

  cancel(): void {
    if (this.isNew) {
      this.router.navigate(['/event-templates']);
    } else {
      this.router.navigate(['/event-templates', this.template?.id]);
    }
  }

  // CanDeactivate guard implementation
  async canDeactivate(): Promise<boolean> {
    if (!this.templateForm.dirty) {
      return true;
    }

    return this.confirmationModal.confirm({
      title: 'Unsaved changes',
      message: 'You have unsaved changes. Are you sure you want to leave this page?',
      confirmLabel: 'Leave Page',
      cancelLabel: 'Stay Here',
      confirmButtonStyle: 'danger',
    });
  }

  // Handle browser refresh/close
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.templateForm.dirty) {
      $event.returnValue = true;
    }
  }
}
